// hooks/useRowMutations.js
// Optimistic-save plumbing for the row-based review pages (Nonstandard Job Names,
// Stale Jobs / Late Filings, Client Data, Stale Clients). A page patches the row
// locally, then runs its POST through `run`, which gives it:
//
//   * Per-row ordering — saves for the same row go out one after another, so rapid
//     clicks (on, off, on…) reach the server in the order they were made. Different
//     rows are independent. Controls are never disabled while a save is in flight.
//   * Failure handling — if it was the only save in flight for the row, `revert()` puts
//     the row back; if others were queued behind it (or an earlier one failed), the
//     page silently refetches once the row's queue drains instead, so a later
//     optimistic edit isn't undone by an earlier row snapshot.
//   * `reconcile` for useSectionData — a heartbeat / poll refetch must not overwrite a
//     row with server data older than the edit: rows with a save in flight, or one that
//     finished after the fetch started, keep their local copy.
//   * `pinned` — rows edited this session stay listed even if the edit makes them stop
//     matching the active filter (e.g. "Needs review" after a sign-off); the page calls
//     `clearPinned` when the user changes a filter.
//
// Usage:
//   const mutations = useRowMutations(r => r.jobIdx);
//   useSectionData(fetcher, { ..., reconcile: mutations.reconcile });
//   mutations.run(row.jobIdx, () => post(...), { revert, refetch: silentRefetch });

import { useCallback, useRef, useState } from 'react';

const TOUCH_TTL_MS = 2 * 60 * 1000;

export function useRowMutations(keyOf) {
  const keyRef = useRef(keyOf);
  keyRef.current = keyOf;
  // tails: key -> promise of the row's last queued save; count: saves queued/in flight;
  // dirty: keys whose burst had a failure we could not revert; touched: key -> last activity ms.
  const st = useRef({ tails: new Map(), count: new Map(), dirty: new Set(), touched: new Map() });
  const [pinned, setPinned] = useState(() => new Set());

  const clearPinned = useCallback(() => setPinned(prev => (prev.size ? new Set() : prev)), []);

  const run = useCallback((rawKey, task, { revert, refetch } = {}) => {
    const key = String(rawKey);
    const s = st.current;
    s.count.set(key, (s.count.get(key) || 0) + 1);
    s.touched.set(key, Date.now());
    setPinned(prev => (prev.has(key) ? prev : new Set(prev).add(key)));

    const job = (s.tails.get(key) || Promise.resolve()).then(async () => {
      try {
        return await task();
      } catch (err) {
        if (s.count.get(key) === 1 && !s.dirty.has(key) && revert) revert();
        else s.dirty.add(key);
        throw err;
      }
    });

    const settled = job.then(() => {}, () => {}).then(() => {
      const left = (s.count.get(key) || 1) - 1;
      s.touched.set(key, Date.now());
      if (left > 0) { s.count.set(key, left); return; }
      s.count.delete(key);
      if (s.tails.get(key) === settled) s.tails.delete(key);
      if (s.dirty.delete(key) && refetch) refetch();
    });
    s.tails.set(key, settled);
    return job;
  }, []);

  // (prev, next, { startedAt }) => merged — see the header. `next` rows whose key is hot keep `prev`'s copy.
  const reconcile = useCallback((prev, next, { startedAt = 0 } = {}) => {
    const s = st.current;
    if (!prev || !Array.isArray(prev.rows) || !next || !Array.isArray(next.rows)) return next;
    if (s.count.size === 0 && s.touched.size === 0) return next;
    const now = Date.now();
    for (const [k, t] of s.touched) if (!s.count.has(k) && now - t > TOUCH_TTL_MS) s.touched.delete(k);
    const prevBy = new Map(prev.rows.map(r => [String(keyRef.current(r)), r]));
    let changed = false;
    const rows = next.rows.map(r => {
      const k = String(keyRef.current(r));
      const hot = s.count.has(k) || (s.touched.get(k) || 0) >= startedAt;
      const mine = hot ? prevBy.get(k) : null;
      if (!mine) return r;
      changed = true;
      return mine;
    });
    return changed ? { ...next, rows } : next;
  }, []);

  // Number of saves queued or in flight for a row (0 = settled).
  const pending = useCallback(rawKey => st.current.count.get(String(rawKey)) || 0, []);

  return { run, reconcile, pending, pinned, clearPinned };
}
